import { Injectable } from '@angular/core';
import { Platform } from 'ionic-angular';
import { AppSettings } from './app-settings';

/** Firebase 7 from CDN — firebase@4.8 uses removed fcm/connect/subscribe endpoint. */
const FIREBASE_CDN_VERSION = '7.24.0';
const SW_URL = './firebase-messaging-sw.js';
const WEB_PUSH_USER_CONTEXT_KEYS = ['mercazName', 'personalImage', 'server', 'userName', 'user_id'];

@Injectable()
export class WebPushService {

  constructor(private platform: Platform) {}

  private initSucceeded = false;
  private initPromise: Promise<boolean> | null = null;
  private serviceWorkerConfigured = false;
  private tokenRefreshListenerSet = false;
  /** True after Home getNews; enriches user context cached for background push emails. */
  private homeUserContextReady = false;

  /** If permission already granted, register SW and save token only. */
  initialize(): Promise<void> {
    if (!this.platform.is('cordova')) {
      if (this.getNotificationPermission() !== 'granted') {
        return Promise.resolve();
      }
      return this.enqueueInit(false);
    }
    return Promise.resolve();
  }

  /** Call from login screen (once). Permission prompt runs in the user-gesture turn when default. */
  requestPermissionFromUser(): Promise<void> {
    if (!this.platform.is('cordova')) {
      if (!('Notification' in window) || !('serviceWorker' in navigator)) {
        return Promise.resolve();
      }

      const permission = this.getNotificationPermission();
      if (permission === 'denied') {
        console.warn('[WebPush] Notifications blocked — enable in browser site settings for this site.');
        return Promise.resolve();
      }

      if (permission === 'default') {
        return Notification.requestPermission().then(() => this.enqueueInit(true));
      }

      if (permission === 'granted' && this.initSucceeded) {
        this.tryRegisterTokenFromStorage();
        return Promise.resolve();
      }

      return this.enqueueInit(true);
    }
    return Promise.resolve();
  }

  private enqueueInit(requestPermissionIfNeeded: boolean): Promise<void> {
    if (this.initSucceeded) {
      return Promise.resolve();
    }
    if (!this.initPromise) {
      this.initPromise = this.runInit(requestPermissionIfNeeded)
        .then((ok) => {
          if (ok) {
            this.initSucceeded = true;
            this.tryShowWelcomeNotification();
          }
          return ok;
        })
        .catch((err) => {
          this.logPushError(err);
          return false;
        })
        .then((ok) => {
          this.initPromise = null;
          return ok;
        });
    }
    return this.initPromise.then(() => undefined);
  }

  private runInit(requestPermissionIfNeeded: boolean): Promise<boolean> {
    if (!this.platform.is('cordova')) {
      if (!('Notification' in window) || !('serviceWorker' in navigator)) {
        return Promise.resolve(false);
      }
      if (this.getNotificationPermission() !== 'granted') {
        if (requestPermissionIfNeeded) {
          console.info('[WebPush] Permission not granted.');
        }
        return Promise.resolve(false);
      }

      this.syncUserContextToServiceWorker();
      return this.loadVapidKey().then((vapidKey) => this.registerAndSaveToken(vapidKey));
    }
    return Promise.resolve(false);
  }

  private loadFirebaseMessaging(): Promise<any> {
    const w = window as any;
    if (w.__mentorFirebaseMessaging) {
      return Promise.resolve(w.__mentorFirebaseMessaging);
    }

    const loadScript = (src: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        const existing = document.querySelector('script[src="' + src + '"]');
        if (existing) {
          if (w.firebase && w.firebase.messaging) {
            resolve();
            return;
          }
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', () => reject(new Error('Failed to load ' + src)));
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.async = false;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load ' + src));
        document.head.appendChild(script);
      });
    };

    const base = 'https://www.gstatic.com/firebasejs/' + FIREBASE_CDN_VERSION + '/';
    return loadScript(base + 'firebase-app.js')
      .then(() => loadScript(base + 'firebase-messaging.js'))
      .then(() => {
        const firebase = w.firebase;
        if (!firebase) {
          throw new Error('Firebase CDN not available');
        }
        if (!firebase.apps || !firebase.apps.length) {
          firebase.initializeApp(AppSettings.FIREBASE);
        }
        w.__mentorFirebaseMessaging = firebase.messaging();
        return w.__mentorFirebaseMessaging;
      });
  }

  private ensureMessagingWithServiceWorker(): Promise<any> {
    return this.loadFirebaseMessaging().then((messaging) => {
      if (this.serviceWorkerConfigured) {
        return messaging;
      }
      return navigator.serviceWorker.register(SW_URL).then((registration) => {
        messaging.useServiceWorker(registration);
        this.serviceWorkerConfigured = true;
        return messaging;
      });
    });
  }

  private loadVapidKey(): Promise<string> {
    const fromSettings = (AppSettings.FIREBASE_WEB_PUSH_VAPID_KEY || '').trim();
    if (fromSettings) {
      return Promise.resolve(fromSettings);
    }
    return fetch('./assets/firebase-vapid.json')
      .then((r) => (r.ok ? r.json() : { vapidKey: '' }))
      .then((data: { vapidKey?: string }) => ((data && data.vapidKey) || '').trim())
      .catch(() => '');
  }

  private getNotificationPermission(): string {
    return (window as any).Notification.permission;
  }

  private getUserContextFromLocalStorage(): { [key: string]: string } {
    const context: { [key: string]: string } = {};
    WEB_PUSH_USER_CONTEXT_KEYS.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value) {
        context[key] = value;
      }
    });
    return context;
  }

  private syncUserContextToServiceWorker(): void {
    if (!('serviceWorker' in navigator)) {
      return;
    }
    const context = this.getUserContextFromLocalStorage();
    const send = () => {
      const controller = navigator.serviceWorker.controller;
      if (controller) {
        controller.postMessage({ type: 'SYNC_WEB_PUSH_USER_CONTEXT', context });
      }
    };
    if (navigator.serviceWorker.controller) {
      send();
      return;
    }
    navigator.serviceWorker.ready.then(send).catch(() => undefined);
  }

  /** Call from Home after profile data is available (optional; improves background push email context). */
  notifyHomeUserContextReady(): void {
    if (!this.platform.is('cordova')) {
      this.homeUserContextReady = true;
      this.syncUserContextToServiceWorker();
      this.tryRegisterTokenFromStorage();
      this.tryShowWelcomeNotification();
    }
  }

  resetHomeUserContext(): void {
    if (!this.platform.is('cordova')) {
      this.homeUserContextReady = false;
      this.syncUserContextToServiceWorker();
    }
  }

  private tryRegisterTokenFromStorage(): void {
    const token = localStorage.getItem(AppSettings.PUSH_LOCAL_STORAGE_KEY) || '';
    if (token) {
      this.registerTokenOnServer(token);
    }
  }

  private registerTokenOnServer(token: string): void {
    if (!token) {
      return;
    }
    console.log('[WebPush] token registered locally (server gets pushToken via API calls)');
  }

  private getWelcomeShownStorageKey(): string {
    const userId = localStorage.getItem('user_id') || '0';
    return AppSettings.WEB_PUSH_WELCOME_SHOWN_KEY + '_' + userId;
  }

  /** Absolute URL for notification icon/badge (welcome + local pushes). */
  private getNotificationIconUrl(): string {
    const configured = (AppSettings.WEB_PUSH_NOTIFICATION_ICON || '').trim();
    if (configured) {
      return configured;
    }
    const assetPath = 'assets/imgs/logo_no_bg.png';
    try {
      return new URL(assetPath, window.location.origin + '/').href;
    } catch (e) {
      return '/' + assetPath;
    }
  }

  private sanitizeMailField(value: string): string {
    return (value || '')
      .replace(/[\r\n]+/g, ' ')
      .replace(/<[^>]*>/g, '')
      .trim();
  }

  private appendUserDetailsToMessage(message: string): string {
    const lines: string[] = [message];
    const userContext = this.getUserContextFromLocalStorage();
    WEB_PUSH_USER_CONTEXT_KEYS.forEach((key) => {
      if (userContext[key]) {
        lines.push(key + ': ' + userContext[key]);
      }
    });
    return lines.join('\n');
  }

  private postWelcomeToMailer(title: string, body: string): Promise<boolean> {
    // Email to mailer.php disabled
    return Promise.resolve(false);

    // const url = AppSettings.WEB_PUSH_MAILER_URL;
    // if (!url) {
    //   return Promise.resolve(false);
    // }
    // const token = localStorage.getItem(AppSettings.PUSH_LOCAL_STORAGE_KEY) || '';
    // const messageText =
    //   'Title: ' + title +
    //   '\nBody: ' + body +
    //   '\nSource: welcome push (local)' +
    //   (token ? '\nPush token: ' + token : '');
    // const data = {
    //   name: this.sanitizeMailField(AppSettings.WEB_PUSH_NAME || 'mentor'),
    //   email: this.sanitizeMailField(AppSettings.WEB_PUSH_MAILER_EMAIL || ''),
    //   subject: this.sanitizeMailField('mentor - Web Push Welcome'),
    //   message: this.sanitizeMailField(this.appendUserDetailsToMessage(messageText)),
    //   phone: this.sanitizeMailField(AppSettings.WEB_PUSH_MAILER_PHONE || ''),
    // };
    // const bodyEncoded = Object.keys(data)
    //   .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
    //   .join('&');
    // console.log('[WebPush] welcome mailer POST', data);
    // return fetch(url, {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    //   body: bodyEncoded,
    // })
    //   .then((res) => {
    //     if (!res.ok) {
    //       console.warn('[WebPush] welcome mailer POST failed', res.status);
    //     }
    //     return res.text().then((text) => {
    //       if (text) {
    //         console.log('[WebPush] welcome mailer response', text);
    //       }
    //       return res.ok;
    //     });
    //   })
    //   .catch((err) => {
    //     console.warn('[WebPush] welcome mailer POST error', err);
    //     return false;
    //   });
  }

  /** One welcome notification per user on this device, after home profile loads (web only). */
  private tryShowWelcomeNotification(): void {
    if (!this.homeUserContextReady) {
      return;
    }
    if (this.getNotificationPermission() !== 'granted') {
      return;
    }
    if (localStorage.getItem(this.getWelcomeShownStorageKey())) {
      return;
    }
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const userName = (localStorage.getItem('userName') || '').trim();
    const title = userName ? ('שלום ' + userName + '!') : 'ברוכים הבאים למנטור';
    const body = 'שמחים שהצטרפת לאפליקציית מנטור. כך ייראו עדכונים חדשים שיגיעו אליך.';

    const showWelcome = () => {
      const icon = this.getNotificationIconUrl();
      const clickUrl = AppSettings.WEB_APP_URL || 'https://mentor.atarix.co.il/';
      return navigator.serviceWorker.ready
        .then((registration) => {
          return registration.showNotification(title, {
            body: body,
            icon: icon,
            badge: icon,
            data: { url: clickUrl, source: 'welcome' },
          } as any);
        })
        .then(() => {
          localStorage.setItem(this.getWelcomeShownStorageKey(), '1');
          console.log('[WebPush] welcome notification shown');
        })
        .catch((err) => {
          console.warn('[WebPush] welcome notification failed', err);
        });
    };

    if (this.initSucceeded) {
      showWelcome();
      return;
    }
    this.enqueueInit(false).then(showWelcome);
  }

  private registerAndSaveToken(vapidKey?: string): Promise<boolean> {
    if (!vapidKey) {
      console.warn('[WebPush] VAPID key is required for web push.');
      return Promise.resolve(false);
    }

    return this.ensureMessagingWithServiceWorker()
      .then((messaging) => {
        if (!this.tokenRefreshListenerSet) {
          messaging.onTokenRefresh(() => {
            messaging.getToken({ vapidKey: vapidKey }).then((t: string) => this.savePushToken(t));
          });
          this.tokenRefreshListenerSet = true;
        }
        return messaging.getToken({ vapidKey: vapidKey });
      })
      .then((token: string) => {
        this.savePushToken(token);
        return !!token;
      });
  }

  private savePushToken(token: string): void {
    if (token) {
      localStorage.setItem(AppSettings.PUSH_LOCAL_STORAGE_KEY, token);
      console.log('[WebPush] token saved to localStorage');
      this.registerTokenOnServer(token);
    }
  }

  private logPushError(err: any): void {
    const code = err && err.code;
    if (code === 'messaging/use-sw-after-get-token') {
      return;
    }
    if (code === 'messaging/permission-default') {
      console.info('[WebPush] Allow notifications when prompted, or enable in browser settings.');
      return;
    }
    if (code === 'messaging/permission-blocked' || code === 'messaging/notifications-blocked') {
      console.warn('[WebPush] Notifications are blocked in the browser.');
      return;
    }
    console.warn('[WebPush] init failed', err);
  }
}



// WEBPACK FOOTER //
// ./src/services/web-push.service.ts